import { useState, useEffect, useRef } from "react";
import type { RadioApproval } from "@/mocks/radioApprovals";

interface RejectModalProps {
  approval: RadioApproval;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

export default function RejectModal({ approval, onClose, onConfirm }: RejectModalProps) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const handleSubmit = () => {
    if (!reason.trim()) {
      setError("El motivo de rechazo es obligatorio.");
      return;
    }
    onConfirm(reason.trim());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
              <i className="ri-close-circle-line text-red-500 text-sm"></i>
            </div>
            <div>
              <h3 className="text-foreground-950 font-bold text-sm">Rechazar Solicitud</h3>
              <p className="text-foreground-400 text-[10px]">{approval.radioName}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center hover:bg-background-200 transition-colors cursor-pointer">
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          <label className="block text-foreground-700 text-xs font-semibold mb-2">
            Motivo del rechazo <span className="text-red-500">*</span>
          </label>
          <textarea
            ref={inputRef}
            value={reason}
            onChange={(e) => { setReason(e.target.value); setError(""); }}
            placeholder="Explicá por qué se rechaza esta solicitud..."
            rows={4}
            maxLength={500}
            className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100/50 transition-all resize-none"
          ></textarea>
          <p className="text-foreground-300 text-[10px] mt-1 flex justify-between">
            <span>{error ? <span className="text-red-500 font-medium">{error}</span> : "Este campo es obligatorio"}</span>
            <span>{reason.length}/500</span>
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 px-5 py-4 border-t border-background-200">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl bg-background-100 text-foreground-600 font-semibold text-xs hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-bold text-xs hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
          >
            <i className="ri-close-circle-line text-xs"></i>
            Confirmar Rechazo
          </button>
        </div>
      </div>
    </div>
  );
}